import {
    Component,
    OnInit,
    Output,
    EventEmitter,
    ViewChild,
    ViewChildren,
    QueryList,
    ElementRef,
    ChangeDetectorRef,
    Renderer2,
    NgZone,
    inject,
} from '@angular/core';
import { Course as CourseLibrary } from '@app/core/model/library.model';
import { ByocMissionService } from '../../../service/mission.service';
import { PerfectScrollbarDirective } from '@app/shared/perfect-scrollbar/perfect-scrollbar.directive';

interface Coordinates {
    height: number | null;
    width: number | null;
    top: number | null;
    left: number | null;
    marginTop?: number | null;
    marginLeft?: number | null;
}

interface CourseListItem {
    id: string;
    position: number;
    fullData: CourseLibrary;
}

@Component({
    selector: 'app-byoc-mission-step-list',
    templateUrl: './byoc-mission-step-list.component.html',
    styleUrls: ['./byoc-mission-step-list.component.scss'],
    standalone: false
})
export class ByocMissionStepListComponent implements OnInit {
    @Output() loadedList: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('ps', { read: PerfectScrollbarDirective, static: false })
    psRef?: PerfectScrollbarDirective;

    /**
     * list of droppable elements
     */
    @ViewChildren('element') elements!: QueryList<ElementRef>;

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly byocMissionService = inject(ByocMissionService);
    private readonly renderer2 = inject(Renderer2);
    private readonly ngZone = inject(NgZone);

    /**
     * List of Courses with new course
     */
    courses: CourseListItem[] = [];

    /**
     * Dragged Node
     */
    node!: Node | null;

    /**
     * Dragged Node List position
     */
    position!: number;

    /**
     * Dragged Node List position
     */
    elementsPosition!: Coordinates[];

    /**
     * Dragged Node List position
     */
    draggedPosition: number | null = null;

    /**
     * coordinates of dragged element
     */
    coordinates!: Coordinates;

    /**
     * Loaded flat
     */
    loaded = false;

    ngOnInit() {
        this.loaded = true;
        this.setCourses();

        this.changeDetectorRef.detectChanges();
        this.psRef?.scrollToBottom();
        this.changeDetectorRef.detectChanges();
        this.loadedList.emit(true);
    }

    /**
     * Drag Start
     */
    onDragStart(event: DragEvent, draggingCourseId: string) {
        this.ngZone.runOutsideAngular(() => {
            window.document.addEventListener('dragover', this.onDrag, false);
        });

        this.position = this.courses.findIndex((course) => course.id === draggingCourseId);

        this.startGhost(event, <HTMLElement>event.target);
    }

    onDragEnd(event: DragEvent) {
        this.ngZone.runOutsideAngular(() => {
            window.document.removeEventListener('dragover', this.onDrag, false);
        });

        this.endGhost(<HTMLElement>event.target);
    }

    private setCourses() {
        const newCoursesData: CourseListItem[] = [];
        const coursesIds = Object.keys(this.byocMissionService.coursesFormGroup.value);
        let i = 1;

        coursesIds.forEach((id: string) => {
            if (this.byocMissionService.coursesFormGroup.value[id]) {
                const fullData = <CourseLibrary>this.byocMissionService.selectedCoursesFullData.find((course) => course.id === id);
                newCoursesData.push({ id: id, position: i, fullData: fullData });
                i++;
            }
        });

        this.courses = newCoursesData;
    }

    private updateCourses() {
        let i = 1;
        this.courses.forEach((course) => {
            course.position = i;
            i++;
        });
    }

    private onDrag = (event: MouseEvent): void => {
        event.preventDefault();
        this.coordinates.top = event.clientY;
        this.coordinates.left = event.clientX;
        this.renderer2.setStyle(this.node, 'top', event.clientY.toString() + 'px');
        this.renderer2.setStyle(this.node, 'left', event.clientX.toString() + 'px');
        this.checkPositions();
    };

    /**
     * Set Ghost
     */
    private startGhost(event: DragEvent, el: HTMLElement) {
        // start animation
        this.elements.forEach((element: ElementRef) => {
            this.renderer2.addClass(element.nativeElement, 'animated');
        });

        this.elementsPosition = this.elements.map((element: ElementRef) => {
            return {
                height: element.nativeElement.getBoundingClientRect().height + 2,
                width: element.nativeElement.getBoundingClientRect().width + 2,
                top: element.nativeElement.getBoundingClientRect().y,
                left: element.nativeElement.getBoundingClientRect().x,
            };
        });

        // clone node
        this.node = el.cloneNode(true);

        // "remove" element
        this.renderer2.setStyle(el, 'opacity', '0.5');

        this.coordinates = {
            top: -el.offsetHeight,
            left: -el.offsetWidth,
            marginTop: -event.offsetY,
            marginLeft: -event.offsetX,
            width: el.offsetWidth,
            height: el.offsetHeight,
        };

        this.renderer2.addClass(this.node, 'attached-to-pointer');
        this.renderer2.setStyle(this.node, 'margin-left', -event.offsetX + 'px');
        this.renderer2.setStyle(this.node, 'margin-top', -event.offsetY + 'px');
        this.renderer2.setStyle(this.node, 'height', el.offsetHeight.toString() + 'px');
        this.renderer2.setStyle(this.node, 'width', el.offsetWidth.toString() + 'px');
        this.renderer2.setStyle(this.node, 'top', -el.offsetHeight.toString() + 'px');
        this.renderer2.setStyle(this.node, 'left', -el.offsetWidth.toString() + 'px');
        // append cloned visible node
        this.renderer2.appendChild(document.querySelector('body'), this.node);
    }

    private endGhost(el: HTMLElement) {
        // end animation
        this.elements.forEach((element: ElementRef) => {
            this.renderer2.removeClass(element.nativeElement, 'animated');
        });

        // render
        this.changeDetectorRef.detectChanges();

        // move element
        if (this.draggedPosition !== null) {
            this.courses.splice(this.draggedPosition, 0, this.courses.splice(this.position, 1)[0]);
        }

        // show element
        this.renderer2.setStyle(el, 'opacity', '1');
        this.renderer2.removeChild(document.querySelector('body'), this.node);

        // remove transform
        this.elements.forEach((element: ElementRef) => {
            this.renderer2.removeStyle(element.nativeElement, 'transform');
        });

        this.node = null;
        this.coordinates = {
            top: null,
            left: null,
            marginTop: null,
            marginLeft: null,
            width: null,
            height: null,
        };

        this.elementsPosition = [];
        this.updateCourses();
    }

    /**
     * Check dragged element position compare to the rest of elements
     */
    private checkPositions(): void {
        let newIndex = null;

        // get index of hovered element
        this.elements.forEach((el: ElementRef, i: number) => {
            if (i === this.position) {
                return;
            }
            if (this.isNodeAbove(el, i)) {
                newIndex = i;
            }
        });

        this.draggedPosition = newIndex;

        let shift = 0;

        // set styles
        this.elements.forEach((el: ElementRef, i: number) => {
            if (this.draggedPosition === null) {
                this.renderer2.removeStyle(el.nativeElement, 'transform');
            } else if (i >= this.draggedPosition && i < this.position) {
                this.renderer2.setStyle(el.nativeElement, 'transform', 'translateY(60px)');
                shift--;
            } else if (i <= this.draggedPosition && i > this.position) {
                this.renderer2.setStyle(el.nativeElement, 'transform', 'translateY(-60px)');
                shift++;
            } else {
                this.renderer2.removeStyle(el.nativeElement, 'transform');
            }
        });

        // set styles
        this.elements.forEach((el: ElementRef, i: number) => {
            if (i === this.position) {
                this.renderer2.setStyle(el.nativeElement, 'transform', 'translateY(' + shift * 60 + 'px)');
            }
        });
    }

    /**
     * Check if dragged element is above
     */
    private isNodeAbove(el: ElementRef, i: number): boolean {
        // get middle top position of dragged element
        const nodeMiddleTop = <number>this.coordinates.top + <number>this.coordinates.marginTop + <number>this.coordinates.height / 2;
        // get middle top position of dragged element
        const nodeMiddleLeft = <number>this.coordinates.left + <number>this.coordinates.marginLeft + <number>this.coordinates.width / 2;
        // get coordinates of element
        const elCoordinates = this.elementsPosition[i];

        // check if height in range
        if (
            nodeMiddleTop > <number>elCoordinates.top &&
            nodeMiddleTop < <number>elCoordinates.top + <number>elCoordinates.height &&
            nodeMiddleLeft > <number>elCoordinates.left &&
            nodeMiddleLeft < <number>elCoordinates.left + <number>elCoordinates.width
        ) {
            return true;
        }

        return false;
    }
}
